/**
 * ═══════════════════════════════════════════════════════════════
 * SAT Generator 3.0 — useTimer Hook
 * Изолированный хук обратного отсчёта для экзамена.
 * ═══════════════════════════════════════════════════════════════
 */

import { useEffect, useState, useRef, useCallback } from 'react';

interface UseTimerOptions {
    totalSeconds: number;
    onTimeUp?: () => void;
    autoStart?: boolean;
}

export function useTimer({
    totalSeconds,
    onTimeUp,
    autoStart = false,
}: UseTimerOptions) {
    const [remaining, setRemaining] = useState(totalSeconds);
    const [running, setRunning] = useState(autoStart);
    const onTimeUpRef = useRef(onTimeUp);
    onTimeUpRef.current = onTimeUp;
    const firedRef = useRef(false);

    useEffect(() => {
        if (!running || remaining <= 0) return;

        const timer = setInterval(() => {
            setRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    if (!firedRef.current) {
                        firedRef.current = true;
                        setTimeout(() => onTimeUpRef.current?.(), 0);
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [running, remaining]);

    const start = useCallback(() => setRunning(true), []);
    const pause = useCallback(() => setRunning(false), []);
    const reset = useCallback(() => {
        setRemaining(totalSeconds);
        firedRef.current = false;
        setRunning(false);
    }, [totalSeconds]);

    const restoreElapsed = useCallback((elapsedSeconds: number) => {
        setRemaining(Math.max(0, totalSeconds - Math.max(0, elapsedSeconds)));
        firedRef.current = false;
        setRunning(false);
    }, [totalSeconds]);

    const percentage = (remaining / totalSeconds) * 100;
    const isUrgent = remaining <= 60;
    const isExpired = remaining <= 0;

    return {
        remaining,
        percentage,
        isUrgent,
        isExpired,
        running,
        start,
        pause,
        reset,
        restoreElapsed,
    };
}
